/**
 * What a mission IS, on the client (022 / #334).
 *
 * A deliberate mirror of `src/lib/mission-type.ts` on the server. The backend and the client
 * are separate TypeScript projects with no shared package, so the vocabulary cannot be
 * imported across — and inventing a shared package for six string literals would cost more
 * than it saves. What is mirrored is kept to the minimum that the UI genuinely needs:
 *
 *  - the vocabulary and its operator-facing labels, so a picker can offer them;
 *  - which types cannot be flown yet, so the surface can say so BEFORE the operator arms a
 *    launch and is refused;
 *  - the geometry each type is drawn with, which is a client-only concern the server has no
 *    opinion about.
 *
 * The RULES are not mirrored. Coherence between a type and its parameters is checked by the
 * API and by 022's CHECK constraints, and the surface shows the server's refusal verbatim
 * rather than predicting it — a second implementation of the same rule is how the two start
 * disagreeing about which missions are valid.
 */

import type { MissionGeometryShape } from './map-mode';
import {
  MISSION_TYPE_DESCRIPTION,
  MISSION_TYPE_LABEL,
  MISSION_TYPE_UNAVAILABLE,
  MISSION_TYPE_UNAVAILABLE_FALLBACK,
  MISSION_TYPE_UNFLYABLE,
} from './copy';

export const MISSION_TYPES = [
  'hover', 'goto', 'out_and_back', 'route', 'patrol', 'orbit',
] as const;

export type MissionType = (typeof MISSION_TYPES)[number];

/** The words are in `lib/copy/mission.ts`; the COP's own `uppercase` class supplies the caps. */
export const MISSION_TYPE_LABELS = MISSION_TYPE_LABEL;

export const MISSION_TYPE_DESCRIPTIONS = MISSION_TYPE_DESCRIPTION;

/**
 * Types with no flight path yet, and why — mirroring the launch service's own refusal.
 *
 * Shown at PLAN time as a warning, not as a block. An operator may legitimately want the
 * mission on the board before the capability exists; what they must not get is a launch
 * control that looks armed and then refuses. The authority is still the server: this only
 * decides what the surface says in advance.
 */
export const UNFLYABLE_MISSION_TYPES = MISSION_TYPE_UNFLYABLE;

export function isFlyableMissionType(type: MissionType): boolean {
  return UNFLYABLE_MISSION_TYPES[type] === undefined;
}

/**
 * The short reason a PICKER gives for a type it will not offer at all.
 *
 * Deliberately separate copy from `UNFLYABLE_MISSION_TYPES`, which is the reason a mission
 * that ALREADY EXISTS with this type cannot be launched — a sentence an operator reads on a
 * mission already on the board, where "planned and briefed but not launched" is exactly true.
 * On a picker it would not be: compose no longer lets these types be chosen, so promising the
 * operator they can plan one would be a lie told by the control that just refused them.
 *
 * Which types are refused is NOT decided here — `isFlyableMissionType` is still the single
 * answer to that, so a type cannot become unofferable in one place and offerable in the other.
 * This map only supplies the wording, and falls back if a third type is ever added above.
 */
const MISSION_TYPE_UNAVAILABLE_REASONS = MISSION_TYPE_UNAVAILABLE;

/** Why the picker will not offer this type, or null when it will. */
export function missionTypeUnavailableReason(type: MissionType): string | null {
  if (isFlyableMissionType(type)) return null;
  return MISSION_TYPE_UNAVAILABLE_REASONS[type] ?? MISSION_TYPE_UNAVAILABLE_FALLBACK;
}

/**
 * How this type's geometry is collected on the map.
 *
 * The whole reason `MapMode` carries a shape rather than a mission type: six types, two
 * interactions. Everything but an orbit is an ordered list of points.
 */
export function geometryShapeForType(type: MissionType): MissionGeometryShape {
  return type === 'orbit' ? 'circle' : 'waypoints';
}

/**
 * Types that take exactly one stored point. An orbit's is the CENTRE of its circle.
 *
 * HOVER left this set in #373 — see `waypointCountRangeForType` below.
 */
export const SINGLE_POINT_MISSION_TYPES: ReadonlySet<MissionType> = new Set<MissionType>([
  'goto', 'orbit',
]);

/**
 * How many points compose should let the operator place. The compose surface stops accepting
 * clicks at `max` rather than letting them draw a route the API will refuse on save.
 */
export function waypointCountRangeForType(type: MissionType): { min: number; max: number } {
  if (SINGLE_POINT_MISSION_TYPES.has(type)) return { min: 1, max: 1 };
  // OUT & BACK is satisfied by one destination as well as by a path flown before returning, and
  // a HOVER (#373) by one point as well as by a route it holds at the end of — for both, what
  // names the type is the finish action, not a waypoint count. Both keep a minimum of 1: every
  // hover stored before #373 has exactly one point. Mirrors the server's
  // `waypointCountRangeForType`; the two must agree or the panel refuses what the API accepts.
  if (type === 'out_and_back' || type === 'hover') return { min: 1, max: 100 };
  return { min: 2, max: 100 };
}

/**
 * What a new mission is until the operator says otherwise.
 *
 * `route`, matching the column's own DEFAULT in 022 and what every mission written before
 * types existed was. Compose therefore opens behaving exactly as it did.
 */
export const DEFAULT_MISSION_TYPE: MissionType = 'route';

/** Default radius for a new orbit, matching the circle geofence's own default. */
export const DEFAULT_ORBIT_RADIUS_M = 500;
export const MIN_ORBIT_RADIUS_M = 10;
export const MAX_ORBIT_RADIUS_M = 5000;

/**
 * An orbit's circle in the two values that define it, or null when the mission is not one.
 *
 * Rendered beside the type wherever a saved mission is listed or read. Without it an ORBIT is
 * indistinguishable from a GO TO POINT on every surface after compose - both are one stored
 * point - and for this type the radius IS the flight path: 500 m of airspace that nothing on
 * screen predicted. It also makes the launch refusal actionable, since "reduce the radius" is
 * advice an operator can only take if they can see what the radius is.
 *
 * `r 500 m · CW`. Uppercase and abbreviated per the UI conventions; the direction is spelled out
 * for assistive tech by the caller's `title`/`aria-label`, not here.
 */
export function orbitSummary(
  m: { type?: string | null; orbit_radius_m?: number | null; orbit_direction?: string | null },
): string | null {
  if (m.type !== 'orbit') return null;
  const radius = m.orbit_radius_m;
  if (radius == null || !Number.isFinite(radius)) return null;
  const direction = m.orbit_direction === 'ccw' ? 'CCW' : m.orbit_direction === 'cw' ? 'CW' : null;
  return direction ? `r ${radius} m · ${direction}` : `r ${radius} m`;
}
